import { useUIColors } from '@/core/hooks';
import React from 'react';
import { Modal, Platform, Pressable, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Typography } from '../atoms/Typography';

export interface BottomSheetProps {
    visible: boolean;
    onClose: () => void;
    /** iOS-only: fires once the Modal has fully finished its close animation. Use this
     * instead of acting right after onClose if you need to do something (e.g. navigate)
     * that races with the Modal's own dismissal (e.g. router.push right after closing). */
    onDismiss?: () => void;
    title?: string;
    /** Added Width of the grey drag handle bar at the top. Defaults to 36. */
    dragHandleWidth?: number;
    /** Set false to hide the grey drag handle bar. Defaults to true. */
    showDragHandle?: boolean;
    children: React.ReactNode;
}

export function BottomSheet({
    visible,
    onClose,
    onDismiss,
    title,
    dragHandleWidth = 36,
    showDragHandle = true,
    children,
}: Readonly<BottomSheetProps>) {
    const colors = useUIColors();
    const insets = useSafeAreaInsets();

    return (
        <Modal
            visible={visible}
            transparent
            animationType="slide"
            onRequestClose={onClose}
            {...(onDismiss && { onDismiss })}
        >
            <Pressable style={[styles.overlay, { backgroundColor: colors.modalBackdrop }]} onPress={onClose}>
                <Pressable
                    style={[
                        styles.container,
                        {
                            backgroundColor: colors.backgroundLevel1,
                            paddingBottom: Math.max(insets.bottom, 24),
                        },
                    ]}
                    onPress={(e) => e.stopPropagation()}
                >
                    {/* Drag Handle */}
                    {showDragHandle && (
                        <View
                            style={[
                                styles.dragHandle,
                                { width: dragHandleWidth, backgroundColor: colors.outlineInactive },
                            ]}
                        />
                    )}

                    {title && (
                        <Typography variant="title2" color="primary" style={styles.title}>
                            {title}
                        </Typography>
                    )}

                    <View style={styles.content}>
                        {children}
                    </View>
                </Pressable>
            </Pressable>
        </Modal>
    );
}

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        justifyContent: 'flex-end',
    },
    container: {
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        paddingTop: 8,
        paddingBottom: Platform.OS === 'ios' ? 34 : 24,
        paddingHorizontal: 16,
        width: '100%',
        maxHeight: '95%',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: -4 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
        elevation: 16,
    },
    dragHandle: {
        width: 36,
        height: 4,
        borderRadius: 2,
        alignSelf: 'center',
        marginVertical: 10,
    },
    title: {
        textAlign: 'center',
        fontWeight: '700',
        fontSize: 20,
        marginBottom: 16,
    },
    content: {
        gap: 16,
    },
});

export default BottomSheet;
